import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from "react";
import { ButtonGroup, Col, Dropdown, Form, FormCheck, OverlayTrigger, Stack, Tooltip } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Row from 'react-bootstrap/Row';
import ReactDOMServer from 'react-dom/server';
import { FieldErrors, FormProvider, useFieldArray, useForm } from "react-hook-form";
import Select, { MenuListProps, SingleValue, components } from 'react-select';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import { DBMaterialType } from "../database_types";
import { readFormData, readOrderData, readProductData, writeFormData, writeOrderData, writeProductData } from "./StolarzFormCRUD";
import { StolarzFormOptionsDropdown } from './StolarzFormOptionsDropdown';
import { GroupedOption, LoadOrderModal, Option, SaveOrderModal, SummaryModal, defaultProductOption, generateSelectProductOptions, openHrefSkipConfirmation } from './StolarzFormSaveLoadModals';
import { StolarzFormUserDataInput } from './StolarzFormUserDataInput';
import { getMaterial } from './database_helpers';
import { defaultEdge, defaultEdgeCode1mm, defaultForm } from "./stolarz_form_defaults";
import { generateEmail, generatePreviewNoWatch } from './stolarz_form_email';
import { ProductSheetsTable } from './stolarz_form_products_and_sheets';
import { FormData, ProductData, SavedProductData, getValidationSchema } from "./stolarz_form_types";

// const Menu = (props: MenuProps<Option, false, GroupedOption>) => {
//     return (
//         <Fragment>
//             <components.Menu< Option, false, GroupedOption> {...props} >
//                 {/* <div>
//                     <FormCheck style={{}}>
//                         <FormCheck.Label> Szukaj wszędzie
//                             <FormCheck.Input
//                                 checked={true} type="checkbox"
//                             // checked={showPreview} type="checkbox" onChange={(e) => setShowPreview(e.currentTarget.checked)} 
//                             />
//                         </FormCheck.Label>
//                     </FormCheck>
//                 </div> */}
//                 {props.children}
//             </components.Menu>
//         </Fragment>
//     );
// };
// const OptionDropdown = (props: OptionProps<Option>) => {
//     const o = props.options
//     return (
//         // <Tooltip content={'Customise your option component!'}>
//         <Fragment>
//             <components.Option {...props} /> asdasda
//         </Fragment>
//         // </Tooltip>
//     );
// };

export function StolarzForm(props: { materialsDB: DBMaterialType[]; edgesDB: DBMaterialType[]; isModyfingExistingOrder: boolean }) {
    // const [isModyfingExistingOrder, setIsModyfingExistingOrder] = useState(false);
    const [loadedFormData, setLoadedFormData] = useState<FormData | undefined>();
    const [editProductName, setEditProductName] = useState(-1);
    const methods = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        resolver: zodResolver(getValidationSchema(props.materialsDB)),
        defaultValues: useCallback(async () => {
            let saved = readFormData();
            let loadDefault = { ...defaultForm }; // ugh how to deep copy?
            loadDefault.products = [{ ...defaultForm.products[0] }];
            loadDefault.products[0].productName += " 1";
            if (saved) {
                loadDefault.name = saved.name;
                loadDefault.email = saved.email;
                loadDefault.address = saved.address;
                loadDefault.city = saved.city;
                loadDefault.nip = saved.nip;
                loadDefault.postalCode = saved.postalCode;
                loadDefault.summary = saved.summary;
            }
            return loadDefault;
        }, [])
    });
    const register = methods.register;
    const handleSubmit = methods.handleSubmit;
    const errors = methods.formState.errors;
    const control = methods.control;
    const watch = methods.watch;
    const getValues = methods.getValues;
    const onSubmit = (values: FormData) => {
        let json = JSON.stringify(values);
        console.log("Generating file with Values:::", json);
        // saveClientPersonalData(values);
        // downloadOptimikCsv(values);
        // window.location.href = hrefMailto;
        handleShowSummaryModal();
    };

    const onError = (error: FieldErrors<FormData>) => {
        console.log("ERROR:::", error);
    };

    let optionsMaterials = useMemo(() => {
        let optionsMaterials: { value: string; label: string; }[] = [];
        for (const mat of props.materialsDB) {
            optionsMaterials.push({ value: mat.code, label: mat.name + ` #${mat.thickness}mm` }); // we can add more tag like search options
        }
        return optionsMaterials;
    }, [props.materialsDB]);
    let optionsEdges = useMemo(() => {
        let optionsEdges: { value: string; label: string; }[] = [defaultEdge];
        for (const mat of props.edgesDB) {
            optionsEdges.push({ value: mat.code, label: mat.name }); // we can add more tag like search options
        }
        return optionsEdges;
    }, [props.edgesDB]);
    const [defaultEdgeCode, setDefaultEdgeCode] = useState(defaultEdgeCode1mm);
    const { fields, append, remove } = useFieldArray({ name: "products", control });

    // todo switch to noWatch function variants and fix dependency array
    // eslint-disable-next-line
    const mailBody = useMemo(() => generateEmail(watch(), loadedFormData, errors, props), [JSON.stringify(watch()), errors]); // todo how to memo form values

    // eslint-disable-next-line
    const mailPreview = useMemo(() => generatePreviewNoWatch(watch("products"), props), [JSON.stringify(watch("products")), watch("summary"), watch("name"), watch("nip"), watch("email"), watch("address"), watch("postalCode"), watch("city")]); // todo how to memo form values
    const htmlMailBody = ReactDOMServer.renderToString(mailBody);
    // todo create ordinary (plain text, non html) email body
    let hrefMailto = `mailto:biuro@stolarz.net.pl?cc=${watch(("email"))}&subject=${encodeURIComponent("Zamówienie formatki")}&body=${encodeURIComponent(htmlMailBody)}&html-body=${encodeURIComponent(htmlMailBody)}`;

    const [showPreview, setShowPreview] = useState<boolean>(true);
    const [showSummaryPreview, setShowSummaryPreview] = useState<boolean>(false);

    const [selectProduct, setSelectProduct] = useState<Option[]>([defaultProductOption]);
    const [selectMaterialCodeFilter, setSelectMaterialCodeFilter] = useState<{ code: string; use: boolean; }[]>([]);
    const [optionsSelectProduct, setSelectOptions] = useState<GroupedOption[]>(() => generateSelectProductOptions(""));
    useEffect(() => {
        let opts: Set<string> = new Set();
        for (const gr of optionsSelectProduct) {
            for (const op of gr.options) {
                if (op.value === "") { // do not include default values
                    continue;
                }
                for (const sh of op.product.sheets) {
                    opts.add(sh.materialCode);
                }
            }
        }
        setSelectMaterialCodeFilter(Array.from(opts).map(code => ({ use: true, code: code })));
    }, [optionsSelectProduct]);
    const handleInputChangeSelectProduct = (input: string) => {
        const options = generateSelectProductOptions(input);
        setSelectOptions(options);
    };
    const [showSave, setShowSave] = useState(false);
    const handleCloseSaveModal = () => setShowSave(false);
    const handleShowSaveModal = () => setShowSave(true);
    const [showLoad, setLoad] = useState(props.isModyfingExistingOrder);
    const handleCloseLoadModal = () => setLoad(false);
    const handleShowLoadModal = () => setLoad(true);
//     const [showSendMail, setShowSendMail] = useState(false);
//     const handleCloseSendMailModal = () => setShowSendMail(false);
//     const handleShowSendMailModal = () => setShowSendMail(true);
    const [showSummary, setShowSummary] = useState(false);
    const handleCloseSummaryModal = () => setShowSummary(false);
    const handleShowSummaryModal = () => { setShowSummary(true); methods.trigger(); handleSaveFormUserData(); }

    const [clearEdgeCodeCacheHooks] = useState<(() => void)[]>([]);

    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            // todo if the form is submitted but not saved (manually) the data will be lost
            if (openHrefSkipConfirmation) {
                console.debug("Opening email, no confirmation needed");
                return;
            }
            if ((methods.formState.isDirty || methods.formState.isSubmitted)) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, [methods.formState.isDirty, methods.formState.isSubmitted]);

    const formatOptionLabel = (option: Option) => {
        const renderTooltip = (_props: any) => {
            let prev = null;
            if (option.product) {
                prev = generatePreviewNoWatch([option.product], props);
            }
            return <Tooltip className="preview" id="button-tooltip" {..._props}> {prev} </Tooltip>;
        };
        const materials = Array.from(new Set(option.product?.sheets.map((sheet) => sheet.materialCode)));
        return <OverlayTrigger placement="auto" delay={{ show: 250, hide: 50 }} overlay={renderTooltip}>
            <div style={{ display: "flex" }}>
                <div>{option.label}
                    <small className='text-muted'> (x{option.product?.amount},
                        kolory: [{materials.map((code, index) => {
                            const mat = getMaterial(props.materialsDB, code);
                            if (mat) {
                                return mat.name + ((index === materials.length - 1) ? "" : ", ");
                            } else {
                                return <><span className="text-danger">Kolor {code} usunięty</span>{((index === materials.length - 1) ? "" : ", ")}</>;
                            }
                        })}]
                        )</small>
                </div>
            </div>
        </OverlayTrigger>;
    };

    const MenuList = (menuListProps: MenuListProps<Option, false, GroupedOption>) => {
        // console.log("sel props", props.selectProps)
        return (
            <components.MenuList {...menuListProps}>
                <div style={{ padding: "8px 12px" }} className='container'>
                    {/* <div style={{ color: "red" }}>Custom Menu List</div> */}
                    {selectMaterialCodeFilter?.map((code, index) => <>
                        <span style={{ padding: 0, display: "inline-block", }}>
                            <FormCheck.Input checked={code.use} type="checkbox" onChange={(e) => {
                                let newList = [...selectMaterialCodeFilter];
                                newList[index].use = !code.use;
                                setSelectMaterialCodeFilter(newList);
                            }} />
                            <FormCheck.Label style={{ paddingRight: 5, paddingBottom: 0 }}> <small className='text-muted'>{getMaterial(props.materialsDB, code.code)?.name} </small>
                            </FormCheck.Label>
                        </span>

                    </>)}
                </div>
                {menuListProps.children}
            </components.MenuList>
        );
    };
    const filterOptions = (option: FilterOptionOption<Option>, input: string) => {
        if (option.value === "") { // default always visible
            return true;
        }
        const matCodes = option.data.product.sheets.map(sh => sh.materialCode);
        return selectMaterialCodeFilter.some((filter) => filter.use && matCodes.find((mat) => mat === filter.code));
    };

    function handleDuplicateProduct(productData: ProductData) {
        const productCopy = { ...productData };
        productCopy.productName += " - kopia";
        // todo should be insert but it breaks boolean useGrain 
        append(productCopy);
    }

    function handleLoadSelectedProdcuts() {
        for (const option of selectProduct) {
            let productCopy = { ...option.product };
            if (!option.orderSaveName) {
                productCopy.productName += ` ${watch("products").length + 1}`;
            }
            append(productCopy);
        }
    }

    function handleSaveFormUserData(): void {
        return writeFormData(watch());
    }


    return <>
            <OverlayTrigger placement='bottom-start' overlay={<Tooltip>Twoje dane będą zapisane lokalnie w twojej przeglądarce po naciśnięciu "Kliknij tu aby wysłać maila (tylko Firefox)"</Tooltip>}>
                <h1>
                    Dane zamawiającego<small>ⓘ</small>
                    <OverlayTrigger placement='bottom-start' overlay={<Tooltip>Zapisz dane osobiste w przeglądarce lokalnie na komputerze</Tooltip>}>
                        <Button variant='secondary' onClick={handleSaveFormUserData}>
                            🖫
                        </Button>
                    </OverlayTrigger>
                </h1>
            </OverlayTrigger>

            <FormProvider {...methods}>
                <Form onSubmit={handleSubmit(onSubmit, onError)}>

                    <StolarzFormUserDataInput />

                    <Row>
                        <Col>
                            <h1>Zamówienie <StolarzFormOptionsDropdown showPreview={showPreview} setShowPreview={setShowPreview} showSummaryPreview={showSummaryPreview} setShowSummaryPreview={setShowSummaryPreview} defaultEdgeCode={defaultEdgeCode}
                                setDefaultEdgeCode={setDefaultEdgeCode} edgesDB={props.edgesDB} handleShowSaveModal={handleShowSaveModal} handleShowLoadModal={handleShowLoadModal}></StolarzFormOptionsDropdown> </h1>
                            {LoadOrderModal(showLoad, handleCloseLoadModal, setLoadedFormData, methods, props, clearEdgeCodeCacheHooks)}
                            {SaveOrderModal(showSave, handleCloseSaveModal, mailPreview, watch)}
                        </Col>
                    </Row>


                    {fields.map((field, productIndex) =>
                        <Row className='striped'>
                            <Stack direction="horizontal" gap={1}>
                                {/* <Dropdown>
                                <Dropdown.Toggle id="bg-nested-dropdown" variant="outline-secondary" style={{ border: 0 }} size='lg'>
                                    <strong>{`${productIndex + 1}.`}</strong>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item eventKey={1} as={Button}>
                                        <Row>
                                            <Button variant="secondary" size="sm" onClick={() => {
                                                handleDuplicateProduct(watch(`products.${productIndex}`));
                                            }}>Duplikuj zestaw</Button>
                                        </Row>
                                    </Dropdown.Item>
                                    <Dropdown.Item eventKey={2} as={Button}>
                                        <Row>
                                            <OverlayTrigger overlay={<Tooltip>Możesz zapisać zestaw, ale będzie ona dostępna tyko na tym komputerze.</Tooltip>}>
                                                <Button variant="info" size="sm" onClick={() => { handleSaveProduct(getValues(`products.${productIndex}`)); }}>
                                                    🖫 Zapisz "{watch(`products.${productIndex}.productName`)}" na tym komputerze
                                                </Button>
                                            </OverlayTrigger>
                                        </Row>
                                    </Dropdown.Item>
                                    <Dropdown.Divider></Dropdown.Divider>
                                    <Dropdown.Item eventKey={3} as={Button}>
                                        <Row> <Button variant="danger" size="sm" onClick={() => remove(productIndex)}>✖ Usuń</Button> </Row>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown> */}

                                <h2>{`${productIndex + 1}.`}</h2>
                                {editProductName !== productIndex ? <>
                                    <h2> {" " + watch(`products.${productIndex}.productName`)} </h2>
                                    <Dropdown as={ButtonGroup}>
                                        <Button variant="outline-secondary" style={{ border: 0 }} size="lg" onClick={() => setEditProductName(productIndex)}>✎</Button>
                                        <Dropdown.Toggle id="bg-nested-dropdown" variant="outline-secondary" style={{ border: 0 }} size='lg'>
                                            {/* <strong>{`${productIndex + 1}.`}</strong> */}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item eventKey={1} as={Button}>
                                                <Row>
                                                    <Button variant="secondary" size="sm" onClick={() => {
                                                        handleDuplicateProduct(watch(`products.${productIndex}`));
                                                    }}>Duplikuj zestaw</Button>
                                                </Row>
                                            </Dropdown.Item>
                                            <Dropdown.Item eventKey={2} as={Button}>
                                                <Row>
                                                    <OverlayTrigger overlay={<Tooltip>Możesz zapisać zestaw, ale będzie ona dostępna tyko na tym komputerze.</Tooltip>}>
                                                        <Button variant="info" size="sm" onClick={() => { handleSaveProduct(getValues(`products.${productIndex}`)); }}>
                                                            🖫 Zapisz "{watch(`products.${productIndex}.productName`)}" na tym komputerze
                                                        </Button>
                                                    </OverlayTrigger>
                                                </Row>
                                            </Dropdown.Item>
                                            <Dropdown.Divider></Dropdown.Divider>
                                            <Dropdown.Item eventKey={3} as={Button}>
                                                <Row> <Button variant="danger" size="sm" onClick={() => remove(productIndex)}>✖ Usuń</Button> </Row>
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </> :
                                    <Form.Control size="lg" placeholder={`Zestaw ${productIndex}`}
                                        key={`Zestaw ${productIndex}`}
                                        type="text" {...register(`products.${productIndex}.productName`)}
                                        onBlur={() => setEditProductName(-1)} />}
                                <div className='p-1 ms-auto'>
                                    <Form.Label column>Ilość</Form.Label>
                                </div>
                                <div className='p-1'>
                                    <Form.Control type="number" min={0} {...register(`products.${productIndex}.amount`, { valueAsNumber: true })} />
                                    {errors.products?.[productIndex]?.amount &&
                                        <Form.Text className="text-danger"> {errors.products?.[productIndex]?.amount?.message?.toString()} </Form.Text>}
                                </div>
                            </Stack>
                            <ProductSheetsTable optionsEdges={optionsEdges} optionsMaterials={optionsMaterials} showPreview={showPreview}
                                productIndex={productIndex} defaultEdgeCode={defaultEdgeCode} clearEdgeCodeCacheHooks={clearEdgeCodeCacheHooks} materialsDB={props.materialsDB}></ProductSheetsTable>
                        </Row>
                    )}
                    <Row><p></p></Row>
                    <Row>
                        <Col className="d-grid">
                            <ButtonGroup className='btn-block'>
                                <Button className="btn-block" style={{ maxWidth: 250, minWidth: 180 }} variant="secondary" onClick={handleLoadSelectedProdcuts}>
                                    🗁 Dodaj wybrany:
                                </Button>
                                <div style={{ minWidth: 300 }} className='w-100'>
                                    <Select<Option, false, GroupedOption>
                                        styles={{ menuList: (provided) => ({ maxHeight: "none" }) }}
                                        placeholder="Wybierz produkt do dodania..."
                                        options={optionsSelectProduct}
                                        defaultValue={[defaultProductOption]}
                                        onInputChange={handleInputChangeSelectProduct}
                                        onMenuOpen={() => handleInputChangeSelectProduct("")}
                                        // onChange={(val: MultiValue<Option>) => setSelectProduct(val.map(option => option))}
                                        onChange={(val: SingleValue<Option>) => setSelectProduct([val || defaultProductOption])}
                                        formatOptionLabel={formatOptionLabel}
                                        value={selectProduct}
                                        // components={{ Menu: Menu }}
                                        components={{ MenuList: MenuList }}
                                        // closeMenuOnSelect={false}
                                        // isMulti
                                        // https://react-select.com/advanced#custom-filter-logic
                                        filterOption={filterOptions} />
                                </div>
                                <Button style={{ maxWidth: 50 }} variant="danger" onClick={() => {
                                    deleteSavedProducts(selectProduct);
                                    handleDeleteSavedProductsFromOrders(selectProduct);
                                    setSelectProduct([]);
                                    handleInputChangeSelectProduct("");
                                }}>
                                    ✖
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>


                    {showSummaryPreview && <> <br></br> {mailPreview} </>} <br></br>

                    <Row>
                        <Dropdown as={ButtonGroup} className="btn-block">
                            <Button type="submit" className="btn-block" variant="primary" size='lg'>
                                Sprawdź błędy i przejdź do podsumowania
                            </Button>

                            <Dropdown.Toggle split id="dropdown-split-basic-show-summary-anyways" style={{ maxWidth: 100 }} />

                            <Dropdown.Menu flip={true} align='end'>
                                <Dropdown.Item>
                                    <Button className="btn-block" variant="primary" size='lg' onClick={handleShowSummaryModal}>
                                        Przejdź do podumowanie pomimo błędów
                                    </Button>
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Row>
                    {SummaryModal(showSummary, handleCloseSummaryModal, watch(), methods, props, mailBody, htmlMailBody, hrefMailto)}
                </Form>
            </FormProvider>
    </>;
}

function handleSaveProduct(product: ProductData) {
    let productDataToSave: ProductData = { ...product }
    productDataToSave.amount = 1;
    const date = new Date();
    productDataToSave.productName = productDataToSave.productName + ` (${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()} ${date.getHours()}:${date.getMinutes()})`;

    let savedProducts = readProductData();
    if (!savedProducts) {
        writeProductData([productDataToSave])
        return;
    }

    while (savedProducts.findIndex((item_ => item_.productName === productDataToSave.productName)) !== -1) {
        productDataToSave.productName = productDataToSave.productName + " - copy";
    }
    savedProducts.push(productDataToSave);
    writeProductData(savedProducts)
}

function handleDeleteSavedProductsFromOrders(selectProduct: Option[]) {
    const savedDataOrders = readOrderData();
    if (!savedDataOrders) {
        return;
    }
    const saved = savedDataOrders;
    let newSaved: { [id: string]: ProductData[] } = {};
    for (const orderSaveName in saved) {
        const savedProducts: ProductData[] = saved[orderSaveName];
        let filtered = savedProducts;
        for (const option of selectProduct) {
            if (option.orderSaveName === orderSaveName) {
                // filter all matching options
                filtered = filtered.filter((savedProduct: SavedProductData) => option?.product.productName !== savedProduct.productName);
            }
        }
        if (filtered && filtered.length) {
            newSaved[orderSaveName] = filtered;
        }
    }
    writeOrderData(newSaved);
}

function deleteSavedProducts(selectProduct: Option[]) {
    const savedDataProducts = readProductData();
    if (!savedDataProducts) {
        return;
    }
    let savedProducts = savedDataProducts;
    let opt: Option | undefined;
    for (const option of selectProduct) {
        if (option.orderSaveName === undefined) {
            // product saved manually
            opt = option;
        }
    }
    console.log("opts :", selectProduct);
    console.log("to remove:", opt?.product.productName);
    const filtered = savedProducts.filter((savedProduct: SavedProductData) => opt?.product.productName !== savedProduct.productName);
    writeProductData(filtered);
}

